import React from 'react';
import { Package, Coins, ClipboardList, Layers, DoorOpen, BookMarked } from 'lucide-react';
import { groupByPhase, sum } from './materialUtils';
import { ROOM_LABELS } from './model3d';
import { fmt } from '../../../lib/projectVisuals';

export const VIEWS = [
  { group: 'Mängder', items: [{ key: 'material', label: 'Material', icon: Package }, { key: 'costs', label: 'Kostnader', icon: Coins }, { key: 'summary', label: 'Sammanställning', icon: ClipboardList }] },
  { group: 'Detaljerad vy', items: [{ key: 'parts', label: 'Byggdelar', icon: Layers }, { key: 'rooms', label: 'Rum', icon: DoorOpen }, { key: 'refs', label: 'Referenser', icon: BookMarked }] },
];

export const ViewNav = ({ view, setView }) => (
  <div className="lg:w-44 flex-shrink-0 flex lg:block gap-1 overflow-x-auto op-noscroll" data-testid="material-view-nav">
    {VIEWS.map((g) => (
      <div key={g.group} className="lg:mb-5 flex lg:block gap-1">
        <p className="hidden lg:block text-[12px] font-bold text-stone-800 mb-1.5 px-2">{g.group}</p>
        {g.items.map((it) => (
          <button key={it.key} onClick={() => setView(it.key)} data-testid={`material-view-${it.key}`}
            className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-2 text-[12.5px] w-full transition-colors ${view === it.key ? 'bg-[#F26B1F]/10 text-[#E05A10] font-semibold' : 'text-stone-500 hover:bg-stone-100'}`}>
            <it.icon size={14} />{it.label}
          </button>
        ))}
      </div>
    ))}
  </div>
);

export const CostView = ({ materials }) => {
  const groups = groupByPhase(materials).map((g) => ({ ...g, total: sum(g.rows) }));
  const max = Math.max(...groups.map((g) => g.total), 1);
  const all = groups.reduce((s, g) => s + g.total, 0);
  return (
    <div className="space-y-2.5" data-testid="material-cost-view">
      {groups.sort((a, b) => b.total - a.total).map((g) => (
        <div key={g.name}>
          <div className="flex justify-between text-[12.5px]"><span className="font-medium">{g.name}</span><span className="op-num font-semibold">{fmt(g.total)} kr <span className="text-stone-400 font-normal">· {Math.round((g.total / (all || 1)) * 100)}%</span></span></div>
          <div className="mt-1 h-2.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#F26B1F] to-[#FF9B5E]" style={{ width: `${(g.total / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

export const SummaryView = ({ plan }) => {
  const rb = plan.reasoning_breakdown || {};
  return (
    <div className="space-y-4 text-sm" data-testid="material-summary-view">
      <p className="leading-relaxed text-stone-700">{plan.ai_summary || 'Ingen sammanfattning.'}</p>
      {!!(rb.key_assumptions || []).length && <div><p className="op-label mb-1.5">Antaganden</p><ul className="list-disc pl-5 space-y-1 text-stone-700">{rb.key_assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul></div>}
      {rb.environmental_notes && <div><p className="op-label mb-1.5">Miljö</p><p className="text-stone-700">{rb.environmental_notes}</p></div>}
    </div>
  );
};

export const PartsView = ({ materials, onPhaseClick }) => (
  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5" data-testid="material-parts-view">
    {groupByPhase(materials).map((g, i) => (
      <button key={g.name} onClick={() => onPhaseClick(g.name)} className="rounded-xl border border-stone-200 p-3.5 text-left hover:border-[#F26B1F] transition-colors" data-testid={`part-card-${i}`}>
        <p className="text-[11px] text-stone-400 font-semibold">Byggdel {i + 1}</p>
        <p className="text-sm font-semibold mt-0.5">{g.name}</p>
        <p className="text-xs text-stone-500 mt-1 op-num">{g.rows.length} rader · {fmt(sum(g.rows))} kr</p>
      </button>
    ))}
  </div>
);

export const RoomsView = ({ model }) => (!model ? <p className="text-sm text-stone-400">Rumsförteckningen visas när 3D-modellen är klar.</p> : (
  <div className="overflow-x-auto rounded-xl border border-stone-200" data-testid="material-rooms-view">
    <table className="w-full text-[12.5px]">
      <thead className="bg-stone-50 text-[11px] text-stone-500"><tr><th className="text-left py-2 pl-3">Plan</th><th className="text-left">Rum</th><th className="text-left">Typ</th><th className="text-right pr-3">Yta</th></tr></thead>
      <tbody>{model.floors.flatMap((f) => f.rooms.map((r, i) => (
        <tr key={`${f.level}-${i}`} className="border-t border-stone-100"><td className="py-2 pl-3 text-stone-500">{f.name}</td><td className="font-medium">{r.name}</td><td className="text-stone-500">{ROOM_LABELS[r.type]}</td><td className="text-right pr-3 op-num">{(r.w * r.d).toFixed(1)} m²</td></tr>
      )))}</tbody>
    </table>
  </div>
));

export const RefsView = ({ materials }) => {
  const map = {};
  materials.forEach((m) => (m.where_to_buy || 'Ej angivet').split(/[,/]| eller /).map((s) => s.trim()).filter(Boolean).slice(0, 3).forEach((s) => {
    map[s] = map[s] || { n: 0, total: 0 }; map[s].n += 1; map[s].total += m.estimated_total_price || 0;
  }));
  return (
    <div className="grid sm:grid-cols-2 gap-2.5" data-testid="material-refs-view">
      {Object.entries(map).sort((a, b) => b[1].total - a[1].total).slice(0, 24).map(([name, v]) => (
        <div key={name} className="rounded-xl border border-stone-200 p-3"><p className="text-sm font-semibold truncate">{name}</p><p className="text-xs text-stone-500 op-num">{v.n} rader · {fmt(v.total)} kr</p></div>
      ))}
    </div>
  );
};
